import React from "react";
import { Box, Stack, Typography } from "@mui/material";

interface SectionHeaderProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  icon?: React.ElementType;
  action?: React.ReactNode;
  /** `page` for in-page section titles, `card` for the header row of a card. */
  variant?: "page" | "card";
  mb?: number | object;
}

/** Title row with optional icon, muted subtitle and a right-aligned action. */
export const SectionHeader: React.FC<SectionHeaderProps> = ({
  title,
  subtitle,
  icon: Icon,
  action,
  variant = "card",
  mb,
}) => {
  if (variant === "page") {
    // In-page section: title left, context + action right; stacked on phones so nothing truncates.
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: action ? "row" : "column", sm: "row" },
          alignItems: { xs: action ? "center" : "flex-start", sm: "flex-end" },
          justifyContent: "space-between",
          columnGap: 2,
          rowGap: 0.5,
          mb: mb ?? { xs: 1.75, md: 2.25 },
          px: { md: 0.5 },
          minWidth: 0,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="h2"
            sx={{ fontSize: { xs: "1.1875rem", md: "1.3125rem" }, fontWeight: 700, letterSpacing: "-0.015em", lineHeight: 1.25 }}
          >
            {title}
          </Typography>
          {subtitle && action && (
            <Typography sx={{ display: { sm: "none" }, fontSize: "0.8125rem", color: "text.secondary", mt: 0.25 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
          {subtitle && (
            <Typography
              sx={{
                fontSize: "0.8125rem",
                color: "text.secondary",
                minWidth: 0,
                textAlign: { sm: "right" },
                // Two lines at most on phones, one line from tablets up.
                overflow: "hidden",
                display: action ? { xs: "none", sm: "-webkit-box" } : "-webkit-box",
                WebkitLineClamp: { xs: 2, sm: 1 },
                WebkitBoxOrient: "vertical",
              }}
            >
              {subtitle}
            </Typography>
          )}
          {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
        </Stack>
      </Box>
    );
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={2}
      sx={{ mb: mb ?? { xs: 1.5, md: 2 }, minWidth: 0 }}
    >
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
        {Icon && (
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: "10px",
              display: "grid",
              placeItems: "center",
              bgcolor: "rgba(205,160,117,0.12)",
              border: "1px solid rgba(205,160,117,0.26)",
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: 16, color: "#F1D3AF" }} />
          </Box>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="h3"
            sx={{ fontSize: "1rem", fontWeight: 650, letterSpacing: "-0.01em", lineHeight: 1.3 }}
            noWrap
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" component="div" noWrap sx={{ mt: 0.25 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Stack>
      {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
    </Stack>
  );
};

export default SectionHeader;
